import { DocumentType, ExtractionStatus } from "generated-graphql/graphql";

/**
 * Default omnisearch for the Staff Manifest Documents page. Includes both the
 * structured RCRA manifest documents and the EPA printed-copy scans
 * (`RCRAManifestPrintedCopy`) so staff can trigger a parse on the latter. The
 * `documentType` enum filter accepts comma-separated values (IN semantics).
 * Returns an empty string when there is no tenant context.
 */
export function buildManifestDocumentsSearch(tenantId: string): string {
  if (!tenantId) return "";
  return `tenantId:${tenantId} documentType:${DocumentType.RcraManifest},${DocumentType.RcraManifestPrintedCopy}`;
}

/**
 * Label for the per-row parse action. A never-parsed document (e.g. an ingested
 * printed copy at `NOT_ATTEMPTED`) is a first parse — "Parse with AI". Anything
 * already parsed is a "Re-parse".
 */
export function reparseMenuLabel(extractionStatus: ExtractionStatus | null | undefined): string {
  return extractionStatus === ExtractionStatus.NotAttempted ? "Parse with AI" : "Re-parse";
}

/**
 * Provenance of a manifest document for the grid's source indicator.
 * RCRAManifestPrintedCopy is pulled from EPA's RCRAInfo API ("api"/machine);
 * everything else (e.g. RCRAManifest) is a human upload ("human").
 */
export function documentSourceKind(
  documentType: DocumentType | null | undefined,
  // The parse pipeline reclassifies a printed copy to RCRAManifest but preserves
  // the ingested type in originalDocumentType — check both so the source survives parse.
  originalDocumentType?: DocumentType | null
): "api" | "human" {
  const isPrintedCopy =
    documentType === DocumentType.RcraManifestPrintedCopy ||
    originalDocumentType === DocumentType.RcraManifestPrintedCopy;
  return isPrintedCopy ? "api" : "human";
}

/** Manifest tracking number shape: 9 digits + 3 uppercase letters (e.g. 027720484JJK). */
const MTN_PATTERN = /\b\d{9}[A-Z]{3}\b/;

/**
 * Best-effort manifest tracking number derived from a document's storageLink, so
 * the parse override can force correct linkage instead of relying on the AI to
 * read the MTN. Printed-copy ingest stores the MTN in the key
 * (`epa-manifest-printed-copies/{tenant}/{mtn}.pdf`); this also tolerates a
 * date-suffixed filename. Returns null when no MTN-shaped token is found.
 *
 * POC: a persisted `Document.sourceManifestTrackingNumber` column should replace
 * this derivation once we're ready for the migration.
 */
export function mtnFromStorageLink(storageLink: string | null | undefined): string | null {
  if (!storageLink) return null;
  const basename = storageLink.split("/").pop() ?? storageLink;
  return basename.match(MTN_PATTERN)?.[0] ?? null;
}

type FacilityEpaIdStatus = {
  epaId: string;
  isInactive: boolean;
  isPrimaryForFacility: boolean;
};

/**
 * Generator EPA ID for the parse override: the primary, active EPA ID of the
 * document's first linked facility (falling back to any active EPA ID). Lets the
 * parse force the generator instead of leaving it to AI extraction. Returns null
 * when the facility has no usable EPA ID. A printed copy is linked to exactly one
 * facility at ingest, so the first facility is the generator.
 */
export function generatorEpaIdFromFacilities(
  facilities:
    | ReadonlyArray<{ epaIdStatuses?: readonly FacilityEpaIdStatus[] | null }>
    | null
    | undefined
): string | null {
  const statuses = facilities?.[0]?.epaIdStatuses ?? [];
  return (
    statuses.find((s) => s.isPrimaryForFacility && !s.isInactive)?.epaId ??
    statuses.find((s) => !s.isInactive)?.epaId ??
    null
  );
}
